/**
 * 장비불러 앱(JB·MC 웹뷰) 연동 흐름 — $j.F.JbApp
 *
 * 앱과 주고받는 통로는 공용 브리지($j.F.App)다. 브리지는 액션을 보내고 받기만 하고,
 * **무엇을 언제** 앱에 보내고 앱의 회신을 어떻게 반영하는지는 여기서 정한다.
 *   start()               진입 시 1회 — 디바이스 컨텍스트 구독·조회, 페이지코드 통보, 회원번호 동기화, 헬스체크
 *   _applyDeviceContext   디바이스 컨텍스트 회신 반영 — GPS·토큰 서버 저장, 지도 이동, 로그인·가입 폼
 *   _uploadDeviceContext     GPS·토큰·스냅샷 서버 저장 (/m/ajax/member/device, 5분 스로틀)
 *   syncMySettings()      회원 알림음·TTS·진동 설정을 앱에 동기화
 *
 * ⚠ **사이트가 아니라 앱 축이다.** 같은 JB 앱 웹뷰인 /m(회원)·/ma(모바일 관리자)가 둘 다 이 흐름을
 *   부른다 — 앱의 시작주소는 /m 이지만 초기페이지가 모바일관리자인 CS 는 302 로 /ma 에 떨어져
 *   /m 의 JS 가 한 번도 뜨지 않는다. 여기를 /m 전용으로 두면 그 CS 의 푸시토큰·GPS 가 영영 안 쌓인다.
 *   (그래서 파일이 F/Mobile/ 이 아니라 F/ 직속이다)
 * 회원 귀속 차단(대리진입)은 이 파일이 아니라 $j.F.App.isApp() 한 곳이다 (.ai/proxy-login.md §4-4).
 * 악덕알리미 흐름은 $j.F.Devil.App 이다. 이 파일에 두지 않는다.
 */
import Util from '../Common/Util';

// 로그인당 1회 설정동기화 판정용 — 이 로그인에서 동기화를 마친 loginKey (sessionStorage).
// sessionStorage 는 탭 단위라 로그아웃해도 안 지워지므로 회원번호가 아닌 loginKey 로 새 로그인을 가른다.
const SYNC_KEY = 'app:auth:settingsSynced';

export default class JbApp {
    constructor() {
        this.isStart = false; // start() 가 이미 돌았는가 (중복 실행 방지)
    }

    /**
     * 앱 통신 초기화 — 페이지 진입 시 1회 호출.
     * - 중복 실행 방지 (isStart 플래그)
     * - 앱 환경에서만 동작 (웹 환경이면 no-op)
     * - 디바이스 컨텍스트 회신 EventBus 구독 (→ _applyDeviceContext)
     * - 초기 디바이스 컨텍스트 조회 + 회원 동기화
     * - 2초 주기 webviewHealthCheck (도즈모드 감지용)
     */
    start() {
        // 중복실행방지
        if (this.isStart) return;
        this.isStart = true;

        if (!$j.F.App.isApp()) return;  // 웹 환경

        // 앱이 디바이스 컨텍스트(GPS/주소/FCM/버전/모델/회원번호)를 회신하면 서버 저장 + 페이지별 반영
        $j.Common.EventBus.on('$j.F.App', 'app:device:context', (e, data) => {
            this._applyDeviceContext(data);
        });

        // 소셜 인증 결과는 $j.F.Social 모듈이 자체 구독해 처리.

        // this.setPopUpConfig(false);
        $j.F.App.getDeviceContext();  // 디바이스 컨텍스트(GPS/주소/FCM/버전/모델/회원번호) 가져오기
        $j.F.App.setCurrentPage();    // 현재 페이지 컨텍스트 알림 (Android 가 URL 매칭 하드코딩 안 하도록)
        if (MEMBER['num']) {
            $j.F.App.syncSettings({ member: { mbno: Util.int(MEMBER['num']), isAdmin: MEMBER['isTalkAdmin'] } });
        }

        // 웹뷰의 상태를 앱에게 주기적으로 알려준다.
        setInterval(() => {
            $j.F.App.webviewHealthCheck();
        }, 2000);
    }

    /**
     * 디바이스 컨텍스트 반영 — callbackDeviceContext 로 도착한 GPS·토큰·앱버전·모델을 처리한다.
     * (구 전역함수 location_infofrom_app_withModel — 앱이 직접 호출하던 진입점이라 여기로 흡수했다)
     *
     * 페이지별 분기:
     *   일반  : 서버 저장(_uploadDeviceContext) + 열려있는 지도 iframe 위치 이동
     *   로그인: 로그인폼에 토큰·버전·좌표 심기
     *   가입  : 가입폼에 좌표·주소·토큰 심기
     *
     * ⚠️ 로그인 여부는 **웹 세션(MEMBER)** 으로만 본다. 앱이 주는 data.mb_no 는 쓰지 않는다 —
     *    앱은 syncSettings 로 회원번호를 받기 전(= 첫 getDeviceContext)엔 0 을 준다.
     *    회원번호는 서버가 세션 $member 에서 얻으므로 페이로드에 싣지 않는다.
     *
     * @param {Object} data callbackDeviceContext 페이로드 {pos1, pos2, token, mobie_ver, adr, isGPS, modelName, platform?}
     */
    _applyDeviceContext(data) {
        const pos1  = data.pos1;
        const pos2  = data.pos2;
        const token = data.token;
        const adr   = (data.adr === undefined) ? '' : data.adr;
        const modelName = (data.modelName === undefined) ? '' : data.modelName;

        const mbNo  = (typeof MEMBER !== 'undefined' && MEMBER['num']) ? MEMBER['num'] : 0;

        let mobieVer = parseFloat(data.mobie_ver);
        if (isNaN(mobieVer)) mobieVer = 1;

        console.log(`pos1: ${pos1} /pos2:${pos2} /token:${token} /mobie_ver:${mobieVer}/adr:${adr} /userNum:${mbNo}/isGPS:${data.isGPS} /modelName: ${modelName}`);

        // 페이지 분류 — 0:일반, 1:로그인, 2:회원가입
        const href = window.location.href;
        const pageType = (href.indexOf('m/login') > 0) ? 1
                       : (href.indexOf('_sky_m_00_join') > 0) ? 2
                       : 0;

        // 푸시진단 스냅샷 — 앱이 실을지 말지 정한다 (하루 1회 정도). 안 실으면 빈 문자열이라
        // 아래 페이로드에 아무 영향이 없다.
        //
        // 문자열로 넘기는 이유 — 폼 직렬화는 숫자·불리언을 전부 문자열로 바꾼다.
        // 객체를 그대로 넘기면 `importance: 0` 이 `"0"` 이 되어 원본이 뭉개진 채 저장된다.
        const diag = !data.diag ? ''
                   : (typeof data.diag === 'string' ? data.diag : JSON.stringify(data.diag));

        if (pageType === 0) {
            if (mbNo) this._uploadDeviceContext({   // 비로그인 — 저장할 대상이 없다
                'ugps':     pos1 + ',' + pos2,
                'uver':     mobieVer,
                'token':    token,
                'modelname': modelName,
                'diag':     diag,
                // 실행중인 앱 (g5_member_devices.app_code). 구앱은 패키지를 모르므로 빈값 → 서버가 쿠키로 폴백.
                // getPackage() 는 모르면 'JB' 를 주므로 hasPackage() 로 먼저 거른다.
                'app':      $j.F.App.hasPackage() ? $j.F.App.getPackage() : '',
                // 플랫폼 — iOS 앱만 'ios' 를 싣는다. 안 오면(안드로이드) 서버가 android 로 저장한다 (g5_member_devices.mobile_type)
                'platform': data.platform || '',
            });

            // 지도 창이 열려있다면 같이 이동
            const $if = $(`#${G5['MAP_IFRAME_ID']}`);
            if ($if && $if[0] && $if[0].contentWindow) {
                $if[0].contentWindow.moveMap(pos1, pos2);
            }
            return;
        }

        if (pageType === 1) {
            document.loginform.mobie_type.value  = 'android';
            document.loginform.mobie_key.value   = token;
            document.loginform.mobie_ver.value   = mobieVer;
            document.loginform.mobie_locpos.value = pos1 + ',' + pos2;
            return;
        }

        $('#app_ver').val(mobieVer);
        $('#gps1').val(pos1);
        $('#gps2').val(pos2);
        $('#token').val(token);
        $('#addr').val(adr);
        $('#phonegetnumber').val('OK');
    }

    /**
     * GPS·앱버전·모델명·푸시토큰(+실렸으면 푸시진단 스냅샷)을 서버에 저장한다.
     * 토큰이 변경되었거나 5분이 지났으면 갱신한다 (내위치새로고침·스냅샷 동반 시에는 무조건).
     * (구 mobile.js 전역함수 updateAndroidGps — _applyDeviceContext 전용이라 함께 흡수)
     *
     * @param {Object} param /m/ajax/member/device 페이로드 {ugps, uver, token, modelname, diag, app, platform}.
     *                       로그인 회원일 때만 부른다(호출부가 거른다) — 회원번호는 서버가 세션에서 얻는다.
     */
    _uploadDeviceContext(param) {
        console.log('-- updateAndroidGps', param);

        let isGpsUpdate = false;

        // 마지막 갱신 후 5분 경과 여부
        const ts = $j.Common.Store.cookie.get(G5['CK_GPS_TIME']);
        const diffInSeconds = dayjs().diff(dayjs(ts), 'second');
        if (!diffInSeconds || diffInSeconds > 300) isGpsUpdate = true;

        // 푸시토큰 변경 여부
        if ($j.Common.Store.cookie.get(G5['CK_GG_TOKEN']) != param['token']) isGpsUpdate = true;

        // 세줄메뉴 > 위치 페이지의 '내위치새로고침' 은 무조건 갱신
        if (G5['menuCode'] == '1030') isGpsUpdate = true;

        // 푸시진단 스냅샷이 실렸으면 무조건 보낸다. 앱은 하루 한 번 실어 보내는데
        // 하필 그 순간이 5분 스로틀에 걸리면 그날 치가 통째로 버려진다.
        if (param['diag']) isGpsUpdate = true;

        if (!isGpsUpdate) {
            console.log('GPS,TOKEN 갱신 SKIP');
            return;
        }

        $.ajax({ type: 'post', url: '/m/ajax/member/device', data: param });

        $j.Common.Store.cookie.set(G5['CK_GPS_TIME'], dayjs().format('YYYY-MM-DD HH:mm:ss'));
        $j.Common.Store.cookie.set(G5['CK_GG_TOKEN'], param['token']);
    }

    /**
     * 회원 환경설정을 syncSettings 로 앱에 일괄 동기화 (JB).
     * 설정값은 서버에서 MEMBER.app_config(raw) 에 실려오므로(getMemberJs) 추가 ajax 없음.
     * 알림페이지 등 진입 시 호출. (구 /m/ajax/setting/app-config 의 키매핑·검증/클램프를 그대로 재현)
     */
    syncMySettings() {
        if (typeof MEMBER === 'undefined' || !MEMBER || !MEMBER.app_config) return;
        const cfg = MEMBER.app_config;

        let usePushSpeech = parseInt(cfg.use_push_speech, 10);   // 읽어주기 사용 여부
        let useSpeechType = parseInt(cfg.use_speech_type, 10);   // 음성서비스 1:접수만, 2:매칭만
        let readPushSpeed = parseFloat(cfg.read_push_speed);     // 음성 속도
        let readPushPitch = parseFloat(cfg.read_push_pitch);     // 음성 톤
        if (usePushSpeech !== 0 && usePushSpeech !== 1) usePushSpeech = 1;
        if (useSpeechType !== 1 && useSpeechType !== 2) useSpeechType = 1;
        if (!(readPushSpeed >= 0.3 && readPushSpeed <= 5)) readPushSpeed = 1; // NaN/범위 → 1
        if (!(readPushPitch >= 0.3 && readPushPitch <= 5)) readPushPitch = 1;

        const noticeOn   = ((parseInt(cfg.mem_push_recive, 10)   || 0) == 0) ? 'NO' : 'YES'; // 알림소리 사용여부
        const matchingOn = ((parseInt(cfg.mem_soojo_pushget, 10) || 0) == 9) ? 'NO' : 'YES'; // 광꽝소리 사용여부

        $j.F.App.syncSettings({
            push:  { noticeOn, matchingOn, vibrate: cfg.is_vibrate, darkMode: (cfg.is_dark != null ? cfg.is_dark : 0) },
            tts:   { enabled: usePushSpeech, type: useSpeechType, speed: readPushSpeed, pitch: readPushPitch },
            sound: { matchingFn: cfg.push_soundfn, noticeFn: cfg.notisnd },
        });
    }

    /**
     * 환경설정 → 앱 동기화, **로그인당 1회**(loginKey). 값 변경 시엔 설정 화면이 자체 재동기화한다.
     *
     * 판정을 사이트 부트스트랩이 아니라 여기 두는 이유 — /m 과 /ma 가 같은 앱·같은 로그인이라
     * 둘이 각자 세어서는 안 된다. sessionStorage 키를 나눠 들면 사이트를 오갈 때마다 같은
     * 설정을 다시 밀어 넣는다. loginKey 없으면(구앱 세션) 그냥 매번 동기화한다.
     */
    syncMySettingsOncePerLogin() {
        const lk = (typeof MEMBER !== 'undefined' && MEMBER && MEMBER.loginKey) || '';
        if (!lk) { this.syncMySettings(); return; }
        if ($j.Common.Store.session.get(SYNC_KEY) === lk) return;
        $j.Common.Store.session.set(SYNC_KEY, lk);
        this.syncMySettings();
    }
}
